import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { CountryUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type CountryUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name_es: string;
  name_esSelectedValue?: { displayId: string; id: string } | null;
  onName_esChange?: (value: string) => void;
  name_esError?: string;
  name_en: string;
  name_enSelectedValue?: { displayId: string; id: string } | null;
  onName_enChange?: (value: string) => void;
  name_enError?: string;
  name_pt: string;
  name_ptSelectedValue?: { displayId: string; id: string } | null;
  onName_ptChange?: (value: string) => void;
  name_ptError?: string;
  code: string;
  codeSelectedValue?: { displayId: string; id: string } | null;
  onCodeChange?: (value: string) => void;
  codeError?: string;
  currency: string;
  currencySelectedValue?: { displayId: string; id: string } | null;
  onCurrencyChange?: (value: string) => void;
  currencyError?: string;
  demonym_es: string;
  demonym_esSelectedValue?: { displayId: string; id: string } | null;
  onDemonym_esChange?: (value: string) => void;
  demonym_esError?: string;
  demonym_en: string;
  demonym_enSelectedValue?: { displayId: string; id: string } | null;
  onDemonym_enChange?: (value: string) => void;
  demonym_enError?: string;
  demonym_pt: string;
  demonym_ptSelectedValue?: { displayId: string; id: string } | null;
  onDemonym_ptChange?: (value: string) => void;
  demonym_ptError?: string;
  countryUpdateStore: CountryUpdateStore;
};

export const CountryUpdateForm: React.FC<CountryUpdateFormProps> = observer(
  ({
    onSave = () => {},
    onCancel = () => {},
    saving = false,
    showToolbox = true,
    showSaveFooter,
    name_es = "",
    name_esError,
    onName_esChange = () => {},
    name_en = "",
    name_enError,
    onName_enChange = () => {},
    name_pt = "",
    name_ptError,
    onName_ptChange = () => {},
    code = "",
    codeError,
    onCodeChange = () => {},
    currency = "",
    currencyError,
    onCurrencyChange = () => {},
    demonym_es = "",
    demonym_esError,
    onDemonym_esChange = () => {},
    demonym_en = "",
    demonym_enError,
    onDemonym_enChange = () => {},
    demonym_pt = "",
    demonym_ptError,
    onDemonym_ptChange = () => {},
    countryUpdateStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            saveAndCloseLabel={langStore.saveEntity}
            closeLabel={langStore.cancelUpdatedEntity}
            saveForm={() => onSave()}
            saveDisabled={saving}
            closeForm={() => onCancel()}
          />
        )}
        <Box>
          <FormExtras
            entity="Country"
            mode="update"
            values={{}}
            options={{}}
            onPatch={(campo: string, valor: any) => {
              switch (campo) {
                case "name_es":
                  onName_esChange?.(valor);
                  break;
                case "name_en":
                  onName_enChange?.(valor);
                  break;
                case "name_pt":
                  onName_ptChange?.(valor);
                  break;
                case "code":
                  onCodeChange?.(valor);
                  break;
                case "currency":
                  onCurrencyChange?.(valor);
                  break;
                case "demonym_es":
                  onDemonym_esChange?.(valor);
                  break;
                case "demonym_en":
                  onDemonym_enChange?.(valor);
                  break;
                case "demonym_pt":
                  onDemonym_ptChange?.(valor);
                  break;
              }
            }}
          />
          <GroupContainer
            id="null"
            errorText={countryUpdateStore.getCardErrors("null")}
            title={langStore.groupCountryLabel}
            collapsible={false}
          >
            <FormControl fullWidth>
              <InputString
                autoFocus
                value={name_es}
                error={Boolean(name_esError)}
                warning={
                  !name_esError &&
                  Boolean(countryUpdateStore.warnings["name_es"])
                }
                helperText={
                  name_esError ||
                  countryUpdateStore.warnings["name_es"]?.message
                }
                label={langStore.CountryNameLabel + " (es)"}
                onChange={(value: string) => onName_esChange(value)}
                description={langStore.CountryNameDescription}
              />
              <InputString
                value={name_en}
                error={Boolean(name_enError)}
                warning={
                  !name_enError &&
                  Boolean(countryUpdateStore.warnings["name_en"])
                }
                helperText={
                  name_enError ||
                  countryUpdateStore.warnings["name_en"]?.message
                }
                label={langStore.CountryNameLabel + " (en)"}
                onChange={(value: string) => onName_enChange(value)}
                description={langStore.CountryNameDescription}
              />
              <InputString
                value={name_pt}
                error={Boolean(name_ptError)}
                warning={
                  !name_ptError &&
                  Boolean(countryUpdateStore.warnings["name_pt"])
                }
                helperText={
                  name_ptError ||
                  countryUpdateStore.warnings["name_pt"]?.message
                }
                label={langStore.CountryNameLabel + " (pt)"}
                onChange={(value: string) => onName_ptChange(value)}
                description={langStore.CountryNameDescription}
              />
              <InputString
                value={code}
                error={Boolean(codeError)}
                warning={
                  !codeError && Boolean(countryUpdateStore.warnings["code"])
                }
                helperText={
                  codeError || countryUpdateStore.warnings["code"]?.message
                }
                label={langStore.CountryCodeLabel}
                onChange={(value: string) => onCodeChange(value)}
                description={langStore.CountryCodeDescription}
              />
              <InputString
                value={currency}
                error={Boolean(currencyError)}
                warning={
                  !currencyError &&
                  Boolean(countryUpdateStore.warnings["currency"])
                }
                helperText={
                  currencyError ||
                  countryUpdateStore.warnings["currency"]?.message
                }
                label={langStore.CountryCurrencyLabel}
                onChange={(value: string) => onCurrencyChange(value)}
                description={langStore.CountryCurrencyDescription}
              />
              <InputString
                value={demonym_es}
                error={Boolean(demonym_esError)}
                warning={
                  !demonym_esError &&
                  Boolean(countryUpdateStore.warnings["demonym_es"])
                }
                helperText={
                  demonym_esError ||
                  countryUpdateStore.warnings["demonym_es"]?.message
                }
                label={langStore.CountryDemonymLabel + " (es)"}
                onChange={(value: string) => onDemonym_esChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.CountryDemonymDescription}
              />
              <InputString
                value={demonym_en}
                error={Boolean(demonym_enError)}
                warning={
                  !demonym_enError &&
                  Boolean(countryUpdateStore.warnings["demonym_en"])
                }
                helperText={
                  demonym_enError ||
                  countryUpdateStore.warnings["demonym_en"]?.message
                }
                label={langStore.CountryDemonymLabel + " (en)"}
                onChange={(value: string) => onDemonym_enChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.CountryDemonymDescription}
              />
              <InputString
                value={demonym_pt}
                error={Boolean(demonym_ptError)}
                warning={
                  !demonym_ptError &&
                  Boolean(countryUpdateStore.warnings["demonym_pt"])
                }
                helperText={
                  demonym_ptError ||
                  countryUpdateStore.warnings["demonym_pt"]?.message
                }
                label={langStore.CountryDemonymLabel + " (pt)"}
                onChange={(value: string) => onDemonym_ptChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.CountryDemonymDescription}
              />
            </FormControl>
          </GroupContainer>
        </Box>
        {(showSaveFooter ?? !showToolbox) && (
          <FormSaveFooter
            onSave={() => onSave()}
            label={langStore.saveEntity}
            disabled={saving}
          />
        )}
      </Grid>
    );
  }
);
